import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import MsrDocumentsPanel from "../components/msr/MsrDocumentsPanel";
import MsrRecordsPanel from "../components/msr/MsrRecordsPanel";
import { MSR_DATASETS } from "../components/msr/msrConfig";
import { useAuthStore } from "../context/AuthContext";
import { hasPermission } from "../lib/utils/permissions";
import apiClient from "../lib/utils/network-client";

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000/";
const TAB_DETAILS = {
  documents: {
    clause: "Main A–M Index",
    route: "/information-under-msr-clause-b-1-11",
    description: "Manage the main MSR list and choose whether each item is a PDF, written content, or a dynamic page.",
  },
  faculty: {
    clause: "B1",
    route: "/b1",
    description: "Add teaching faculty records. Published records appear under Teaching Faculty on the B1 page.",
  },
  residents: {
    clause: "B1",
    route: "/b1",
    description: "Add tutors and senior residents. Published records appear under Tutors & Senior Residents on the B1 page.",
  },
  non_teaching: {
    clause: "B2",
    route: "/b2",
    description: "Add non-teaching staff records. Published records appear on the B2 page.",
  },
  ug_courses: {
    clause: "C",
    route: "/c",
    description: "Add undergraduate programmes, seat counts, and recognition details for the C page.",
  },
  pg_courses: {
    clause: "C",
    route: "/c",
    description: "Add postgraduate programmes, seat counts, and recognition details for the C page.",
  },
};

const TABS = [
  { key: "documents", label: "MSR Main Page", ...TAB_DETAILS.documents },
  ...Object.entries(MSR_DATASETS).map(([key, value]) => ({
    key,
    label: value.label,
    ...TAB_DETAILS[key],
  })),
];

const getPublicUrl = (route) =>
  `${WEBSITE_URL.replace(/\/+$/, "")}/${route.replace(/^\/+/, "")}`;

const MsrClause = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState("documents");
  const [summary, setSummary] = useState({ counts: {}, documents: {} });
  const canCreate = hasPermission(user, "msr-clause", "create");
  const canEdit = hasPermission(user, "msr-clause", "edit");
  const canDelete = hasPermission(user, "msr-clause", "delete");
  const activeTabDetails = TABS.find((tab) => tab.key === activeTab) || TABS[0];

  useEffect(() => {
    apiClient.get("/msr/admin/summary")
      .then((response) => setSummary(response.data?.data || { counts: {}, documents: {} }))
      .catch(() => {});
  }, [activeTab]);

  return (
    <div className="max-w-[1700px] space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">MSR Clause B.1.11</h1>
        <p className="mt-1 text-sm font-medium text-slate-500">
          Manage the public A–M clause index, teaching staff, non-teaching staff, and sanctioned course intake.
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
        <div className="flex min-w-max gap-1" role="tablist" aria-label="MSR data sections">
          {TABS.map((tab) => {
            const count = tab.key === "documents"
              ? summary.documents?.total
              : summary.counts?.[tab.key]?.total;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg px-4 py-2.5 text-sm font-bold transition ${activeTab === tab.key ? "bg-primary text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
              >
                <span className="flex flex-col items-start gap-0.5">
                  <span>{tab.label}{Number.isFinite(Number(count)) ? ` (${count || 0})` : ""}</span>
                  <span className={`text-[10px] font-semibold ${activeTab === tab.key ? "text-blue-100" : "text-slate-400"}`}>
                    {tab.clause} · {tab.route}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-900">
            {activeTabDetails.clause}: {activeTabDetails.label}
          </p>
          <p className="mt-1 text-sm leading-5 text-slate-600">{activeTabDetails.description}</p>
        </div>
        <a
          href={getPublicUrl(activeTabDetails.route)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-bold text-primary shadow-sm ring-1 ring-blue-100 transition hover:bg-blue-50"
        >
          Public page: {activeTabDetails.route}
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {activeTab === "documents" ? (
        <MsrDocumentsPanel canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} />
      ) : (
        <MsrRecordsPanel
          key={activeTab}
          dataset={activeTab}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}
    </div>
  );
};

export default MsrClause;
